import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useGameStore } from '../store/gameStore';
import { averageMetric, trainingMinutes } from '../utils/resultMetrics';
import { gameCategories } from '../data/games';
import {
  ArrowLeft,
  Clock,
  Calendar,
  TrendingUp,
  Activity,
  Target,
  Zap,
  Trophy,
  Download,
} from 'lucide-react';

export default function ParentEyeReport() {
  const navigate = useNavigate();
  const { levelResults, consecutiveDays, playerName } = useGameStore();

  const gameMeta = useMemo(() => {
    const map = new Map<
      string,
      { name: string; categoryId: string; categoryName: string; icon: string }
    >();

    gameCategories.forEach((category) => {
      category.games.forEach((game) => {
        map.set(game.id, {
          name: game.name,
          categoryId: category.id,
          categoryName: category.name,
          icon: game.icon,
        });
      });
    });

    return map;
  }, []);

  const now = new Date();
  const today = now.toISOString().split('T')[0];
  const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

  const getCompletedAt = (value: unknown) => (typeof value === 'string' ? value : '');

  const weekResults = levelResults.filter((result) => {
    const completedAt = getCompletedAt(result?.completedAt);
    return completedAt ? new Date(completedAt) > weekAgo : false;
  });

  const totalSessions = weekResults.length;
  const trainingDays = new Set(
    weekResults
      .map((item) => getCompletedAt(item?.completedAt).split('T')[0])
      .filter(Boolean),
  ).size;

  const avgAccuracy = averageMetric(weekResults, 'accuracy');
  const avgReaction = averageMetric(weekResults, 'reactionTime');

  const avgScore =
    weekResults.length > 0
      ? weekResults.reduce((sum, item) => sum + item.score, 0) / weekResults.length
      : 0;

  const totalMinutes = trainingMinutes(weekResults);

  const trainingScore = Math.min(
    100,
    Math.round(
      Math.min(40, totalSessions * 3) +
        Math.min(20, trainingDays * 2.5) +
        Math.min(25, avgAccuracy * 0.25) +
        Math.min(15, consecutiveDays * 1.5),
    ),
  );

  const status =
    trainingScore >= 85
      ? { level: '状态优秀', color: 'text-emerald-600', bg: 'bg-emerald-50', desc: '训练节奏稳定，继续保持。' }
      : trainingScore >= 65
        ? { level: '持续进步', color: 'text-amber-600', bg: 'bg-amber-50', desc: '建议提高训练频率，效果会更明显。' }
        : { level: '建议加强', color: 'text-red-500', bg: 'bg-red-50', desc: '本周训练偏少，先从短时高频开始。' };

  const weeklyChartData = Array.from({ length: 7 }, (_, i) => {
    const date = new Date(now.getTime() - (6 - i) * 24 * 60 * 60 * 1000);
    const dayStr = date.toISOString().split('T')[0];
    const dayResults = levelResults.filter((item) => {
      const completedAt = getCompletedAt(item?.completedAt);
      return completedAt ? completedAt.startsWith(dayStr) : false;
    });

    return {
      day: date.toLocaleDateString('zh-CN', { weekday: 'short' }),
      sessions: dayResults.length,
      accuracy:
        dayResults.length > 0
          ? Math.round(averageMetric(dayResults, 'accuracy'))
          : 0,
    };
  });

  const maxSessions = Math.max(...weeklyChartData.map((item) => item.sessions), 1);

  const categoryStats = gameCategories.map((category) => {
    const ids = new Set(category.games.map((item) => item.id));
    const categoryResults = weekResults.filter((item) => ids.has(item.levelId));

    return {
      id: category.id,
      name: category.name,
      icon: category.icon,
      count: categoryResults.length,
      avgAccuracy:
        categoryResults.length > 0
          ? Math.round(averageMetric(categoryResults, 'accuracy'))
          : 0,
    };
  });

  const topGames = useMemo(() => {
    const countMap = new Map<string, number>();

    weekResults.forEach((result) => {
      countMap.set(result.levelId, (countMap.get(result.levelId) || 0) + 1);
    });

    return Array.from(countMap.entries())
      .map(([id, count]) => {
        const meta = gameMeta.get(id);
        return {
          id,
          count,
          name: meta?.name || '训练项目',
          category: meta?.categoryName || '综合训练',
          icon: meta?.icon || '🎮',
        };
      })
      .sort((a, b) => b.count - a.count)
      .slice(0, 4);
  }, [gameMeta, weekResults]);

  const suggestions = [
    totalSessions < 7
      ? '建议每天完成1-2局短时训练，先建立稳定节奏。'
      : '训练频率不错，可继续保持当前节奏。',
    avgAccuracy < 70
      ? '优先选择低难度项目，先把准确率提升到75%以上。'
      : '准确率表现稳定，可逐步加入挑战难度。',
    avgReaction > 850
      ? '可增加追踪、点击类玩法，帮助提升即时反应。'
      : '反应速度表现良好，适合尝试更快节奏玩法。',
  ];

  const handleExport = () => {
    const reportHtml = `
<!DOCTYPE html>
<html lang="zh-CN">
<head><meta charset="UTF-8"><title>游戏训练报告 - ${playerName || '小探险者'}</title>
<style>
body{font-family:system-ui,-apple-system,sans-serif;max-width:640px;margin:0 auto;padding:20px;background:#f8f9fa;color:#333}
h1{text-align:center;color:#059669}
.card{background:#fff;border-radius:16px;padding:18px;margin:14px 0;box-shadow:0 2px 8px rgba(0,0,0,0.05)}
.score{font-size:46px;font-weight:bold;text-align:center;color:#059669}
.grid{display:grid;grid-template-columns:1fr 1fr;gap:10px}
.stat{text-align:center;padding:12px;background:#f0fdf4;border-radius:12px}
.stat-value{font-size:24px;font-weight:bold;color:#059669}
.stat-label{font-size:12px;color:#6b7280;margin-top:4px}
li{margin:8px 0;line-height:1.5}
.footer{text-align:center;color:#9ca3af;font-size:12px;margin-top:20px}
</style></head>
<body>
<h1>🎮 家长游戏训练报告</h1>
<p style="text-align:center;color:#6b7280">生成日期：${now.toLocaleDateString('zh-CN')}</p>
<div class="card"><p style="text-align:center;color:#6b7280">综合训练状态</p><p class="score">${trainingScore}</p></div>
<div class="card"><h3>📊 本周核心数据</h3><div class="grid">
<div class="stat"><div class="stat-value">${trainingDays}/7</div><div class="stat-label">活跃训练天数</div></div>
<div class="stat"><div class="stat-value">${totalSessions}</div><div class="stat-label">训练局数</div></div>
<div class="stat"><div class="stat-value">${Math.round(avgAccuracy)}%</div><div class="stat-label">平均准确率</div></div>
<div class="stat"><div class="stat-value">${Math.round(avgReaction)}ms</div><div class="stat-label">平均反应时间</div></div>
</div></div>
<div class="card"><h3>💡 本周建议</h3><ul>
${suggestions.map((item) => `<li>${item}</li>`).join('')}
</ul></div>
<div class="footer">视界探险 · 家长游戏训练报告 · ${now.toLocaleDateString('zh-CN')}</div>
</body></html>`;

    const blob = new Blob([reportHtml], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `游戏训练报告_${today}.html`;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  const containerAnim = {
    hidden: { opacity: 0 },
    show: { opacity: 1, transition: { staggerChildren: 0.06 } },
  };

  const cardAnim = {
    hidden: { y: 20, opacity: 0 },
    show: { y: 0, opacity: 1, transition: { duration: 0.4 } },
  };

  return (
    <div className="min-h-screen w-full bg-[#f8f9fa] dark:bg-gray-900">
      <header className="sticky top-0 z-30 bg-white/80 dark:bg-gray-800/80 backdrop-blur-md border-b border-gray-100 dark:border-gray-700">
        <div className="flex items-center justify-between max-w-2xl mx-auto px-4 py-3">
          <button
            onClick={() => navigate('/parent')}
            className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-gray-500 dark:text-gray-300"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-base font-bold text-gray-800 dark:text-gray-100">游戏训练报告</h1>
          <button
            onClick={handleExport}
            className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-gray-500 dark:text-gray-300"
            title="导出报告"
          >
            <Download className="w-5 h-5" />
          </button>
        </div>
      </header>

      <motion.div
        variants={containerAnim}
        initial="hidden"
        animate="show"
        className="max-w-2xl mx-auto px-4 py-6 pb-28 space-y-4"
      >
        <motion.div
          variants={cardAnim}
          className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm border border-gray-100 dark:border-gray-700 text-center"
        >
          <p className="text-xs text-gray-400 dark:text-gray-500 mb-4">综合训练状态</p>
          <div className="relative w-36 h-36 mx-auto mb-4">
            <svg className="w-36 h-36 -rotate-90" viewBox="0 0 120 120">
              <circle
                cx="60"
                cy="60"
                r="52"
                fill="none"
                stroke="currentColor"
                strokeWidth="8"
                className="text-gray-100 dark:text-gray-700"
              />
              <motion.circle
                cx="60"
                cy="60"
                r="52"
                fill="none"
                stroke="url(#scoreGradient)"
                strokeWidth="8"
                strokeLinecap="round"
                strokeDasharray={`${2 * Math.PI * 52}`}
                initial={{ strokeDashoffset: 2 * Math.PI * 52 }}
                animate={{ strokeDashoffset: 2 * Math.PI * 52 * (1 - trainingScore / 100) }}
                transition={{ delay: 0.3, duration: 1.2, ease: 'easeOut' }}
              />
              <defs>
                <linearGradient id="scoreGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#34d399" />
                  <stop offset="100%" stopColor="#14b8a6" />
                </linearGradient>
              </defs>
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <motion.span
                className="text-3xl font-bold text-emerald-500"
                initial={{ opacity: 0, scale: 0.5 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.5, duration: 0.4 }}
              >
                {trainingScore}
              </motion.span>
              <span className="text-[10px] text-gray-400">分</span>
            </div>
          </div>
          <div
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium ${status.color} ${status.bg} dark:bg-opacity-20`}
          >
            <Activity className="w-3.5 h-3.5" />
            {status.level}
          </div>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">{status.desc}</p>
        </motion.div>

        <motion.div variants={cardAnim} className="grid grid-cols-2 gap-3">
          {[
            {
              icon: Calendar,
              label: '活跃训练天数',
              value: `${trainingDays}/7天`,
              color: 'text-emerald-600',
              bg: 'bg-emerald-50',
            },
            {
              icon: Clock,
              label: '累计训练时长',
              value: `${totalMinutes}分钟`,
              color: 'text-blue-600',
              bg: 'bg-blue-50',
            },
            {
              icon: Target,
              label: '平均准确率',
              value: `${Math.round(avgAccuracy)}%`,
              color: 'text-violet-600',
              bg: 'bg-violet-50',
            },
            {
              icon: Zap,
              label: '平均反应时间',
              value: `${Math.round(avgReaction)}ms`,
              color: 'text-amber-600',
              bg: 'bg-amber-50',
            },
          ].map((stat, index) => (
            <div
              key={index}
              className="bg-white dark:bg-gray-800 rounded-2xl p-4 shadow-sm border border-gray-100 dark:border-gray-700"
            >
              <div
                className={`w-9 h-9 ${stat.bg} dark:bg-opacity-20 rounded-xl flex items-center justify-center mb-2.5`}
              >
                <stat.icon className={`w-4.5 h-4.5 ${stat.color}`} />
              </div>
              <p className="text-lg font-bold text-gray-800 dark:text-gray-100">{stat.value}</p>
              <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-0.5">{stat.label}</p>
            </div>
          ))}
        </motion.div>

        <motion.div
          variants={cardAnim}
          className="bg-white dark:bg-gray-800 rounded-2xl p-5 shadow-sm border border-gray-100 dark:border-gray-700"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100 flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-emerald-500" />
              近7日训练曲线
            </h3>
            <span className="text-[10px] text-gray-400 dark:text-gray-500">柱高：训练局数</span>
          </div>
          <div className="flex items-end justify-between h-32 gap-2">
            {weeklyChartData.map((item, index) => (
              <div key={index} className="flex flex-col items-center flex-1 gap-1.5">
                <span className="text-[10px] text-gray-500 font-medium">
                  {item.sessions > 0 ? item.sessions : ''}
                </span>
                <motion.div
                  className="w-full rounded-lg bg-gradient-to-t from-emerald-400 to-teal-400"
                  initial={{ height: 0 }}
                  animate={{ height: item.sessions > 0 ? `${(item.sessions / maxSessions) * 100}%` : '4px' }}
                  transition={{ delay: 0.3 + index * 0.06, duration: 0.5, ease: 'easeOut' }}
                  style={{ minHeight: item.sessions > 0 ? '12px' : '4px', opacity: item.sessions > 0 ? 1 : 0.3 }}
                />
                <span className="text-[10px] text-gray-400">{item.day}</span>
              </div>
            ))}
          </div>
        </motion.div>

        <motion.div
          variants={cardAnim}
          className="bg-white dark:bg-gray-800 rounded-2xl p-5 shadow-sm border border-gray-100 dark:border-gray-700"
        >
          <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100 mb-4 flex items-center gap-2">
            <Activity className="w-4 h-4 text-blue-500" />
            分类训练完成度
          </h3>
          <div className="space-y-3">
            {categoryStats.map((category, index) => (
              <div key={category.id} className="flex items-center gap-3">
                <span className="text-lg flex-shrink-0">{category.icon}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="text-gray-600 dark:text-gray-300 font-medium">{category.name}</span>
                    <span className="text-gray-400 dark:text-gray-500">
                      {category.count > 0 ? `${category.count}局 · ${category.avgAccuracy}%` : '本周未训练'}
                    </span>
                  </div>
                  <div className="h-2 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                    <motion.div
                      className="h-full bg-gradient-to-r from-emerald-400 to-teal-400 rounded-full"
                      initial={{ width: 0 }}
                      animate={{ width: `${Math.min(100, category.count * 12)}%` }}
                      transition={{ delay: 0.5 + index * 0.08, duration: 0.6, ease: 'easeOut' }}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        <motion.div
          variants={cardAnim}
          className="bg-white dark:bg-gray-800 rounded-2xl p-5 shadow-sm border border-gray-100 dark:border-gray-700"
        >
          <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100 mb-4 flex items-center gap-2">
            <Trophy className="w-4 h-4 text-amber-500" />
            本周常玩项目
          </h3>
          <div className="space-y-2.5">
            {topGames.length > 0 ? (
              topGames.map((game, index) => (
                <div key={game.id} className="flex items-center justify-between bg-gray-50 dark:bg-gray-700 rounded-xl p-3">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <span className="text-base">{game.icon.slice(0, 2)}</span>
                    <div className="min-w-0">
                      <p className="text-xs font-medium text-gray-700 dark:text-gray-200 truncate">{game.name}</p>
                      <p className="text-[10px] text-gray-400 dark:text-gray-500 truncate">{game.category}</p>
                    </div>
                  </div>
                  <span className="text-xs font-semibold text-emerald-600 dark:text-emerald-400">
                    第{index + 1}名 · {game.count}局
                  </span>
                </div>
              ))
            ) : (
              <div className="text-center py-6 text-xs text-gray-400 dark:text-gray-500">
                本周还没有训练记录，完成一局后就会生成榜单。
              </div>
            )}
          </div>
        </motion.div>

        <motion.div
          variants={cardAnim}
          className="bg-white dark:bg-gray-800 rounded-2xl p-5 shadow-sm border border-gray-100 dark:border-gray-700"
        >
          <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100 mb-4">💡 本周训练建议</h3>
          <div className="space-y-3">
            {suggestions.map((item, index) => (
              <div key={index} className="flex items-start gap-3 bg-gray-50 dark:bg-gray-700/50 rounded-xl p-3.5">
                <div className="w-2 h-2 rounded-full mt-1.5 flex-shrink-0 bg-emerald-400" />
                <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">{item}</p>
              </div>
            ))}
          </div>
          <div className="mt-4 pt-3 border-t border-gray-100 dark:border-gray-600">
            <p className="text-[10px] text-gray-400 dark:text-gray-500 text-center">
              报告生成时间：{now.toLocaleString('zh-CN')}
            </p>
          </div>
        </motion.div>

        <motion.div variants={cardAnim} className="text-center py-2">
          <p className="text-[10px] text-gray-300 dark:text-gray-600">本周平均得分：{Math.round(avgScore)} 分</p>
        </motion.div>
      </motion.div>

      <nav className="fixed bottom-0 left-0 right-0 mx-auto sm:max-w-md md:max-w-lg sm:border-x z-40 bg-white/90 dark:bg-gray-800/90 backdrop-blur-lg border-t border-gray-100 dark:border-gray-700">
        <div className="max-w-2xl mx-auto flex items-center justify-center gap-3 py-3 px-4">
          <button
            onClick={handleExport}
            className="px-6 py-2.5 rounded-xl text-sm font-semibold text-emerald-600 bg-emerald-50 dark:bg-emerald-900/30 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800 hover:bg-emerald-100 transition-all active:scale-95"
          >
            导出报告
          </button>
          <button
            onClick={() => navigate('/parent')}
            className="px-6 py-2.5 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-emerald-500 to-teal-500 shadow-md shadow-emerald-200 dark:shadow-none hover:shadow-lg hover:shadow-emerald-300 transition-all active:scale-95"
          >
            返回面板
          </button>
        </div>
      </nav>
    </div>
  );
}
